import React from 'react';
import moment from 'moment';
import { Bubble } from '@home/components/TeaComponents';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';

import IMG_Rank_01 from '@intelligence/assets/imgs/casualDailyReports/rank01.png';
import IMG_Rank_02 from '@intelligence/assets/imgs/casualDailyReports/rank02.png';
import IMG_Rank_03 from '@intelligence/assets/imgs/casualDailyReports/rank03.png';

export const NEW_TOP = '新进榜';

/**
 * 核心要点md文档中的点击事件类型
 */
export const CasualDailyReportsCorePointsClickAction = {
  /**
   * 点击GRC敏捷报告详情超链接
   */
  ViewReportDetails: 'view-report-details',
};

export const getHostName = (url?: string): string => {
  if (!url?.trim()) {
    return '';
  }

  let hostName = '';

  try {
    const urlData = new URL(url);
    hostName = urlData.hostname;
  } catch (exception) {
    hostName = url;
  }

  return hostName;
};

export const getInitialDate = (params: { dates: string[]; date: string }) => {
  const { dates, date } = params || {};

  if (isEmpty(dates)) {
    return '';
  }

  if (isEmpty(date)) {
    return dates[dates.length - 1];
  }

  const dateM = moment(date);

  if (dateM.isValid()) {
    return dateM.format('YYYY-MM-DD');
  }

  return dates[dates.length - 1];
};

export class CasualDailyReportData {
  private markdownStyle: IRecord = {};

  constructor() {
    this.markdownStyle = {};
  }

  /**
   * 生成替换模式
   * @param {string} tag - HTML 标签名
   * @returns {string} 替换模式字符串
   */
  generateReplacement(tag: string) {
    let replacement = `<${tag}`;

    // 如果存在该标签的样式，添加到标签中
    if (this.markdownStyle?.[tag]) {
      replacement += ` ${this.markdownStyle[tag]}`;
    }

    replacement += `>$1</${tag}>`;
    return replacement;
  }

  /**
   * 带样式的 Markdown 转 HTML
   * @param {string} markdown - Markdown 文本
   * @param {Object} styles - 样式映射对象
   * @param {boolean} processALinker - 是否特殊处理a标签超连接
   * @returns {string} 转换后的 HTML
   */
  public convertMarkdownToHTMLWithStyle(markdown: string, styles: IRecord, processALinker?: boolean) {
    if (!markdown?.trim()) {
      return '';
    }

    const realStyles: IRecord = {};

    // 过滤和转换样式映射
    for (const [key, value] of Object.entries(styles)) {
      if (value !== null && value !== undefined && typeof value === 'string') {
        realStyles[key] = value;
      }
    }

    this.markdownStyle = realStyles;
    return this.convertMarkdownToHTML(markdown, processALinker);
  }

  /**
   * Markdown 转 HTML 核心方法
   * @param {string} markdown - Markdown 文本
   * @param {boolean} processALinker - 是否特殊处理a标签超连接
   * @returns {string} 转换后的 HTML
   */
  public convertMarkdownToHTML(markdown: string, processALinker = false) {
    let html = markdown;

    // ========== 1. 转换超链接 ==========
    if (processALinker) {
      html = convertExtendedMarkdownLink(html);
    }

    // ========== 2. 转换粗体文本 ==========
    // 匹配 **text** 格式
    html = html.replace(/\*\*(.*?)\*\*/g, this.generateReplacement('strong'));

    // ========== 3. 转换标题 (h6 -> h1) ==========
    html = html.replace(/^######\s*(.*)$/gm, this.generateReplacement('h6'));
    html = html.replace(/^#####\s*(.*)$/gm, this.generateReplacement('h5'));
    html = html.replace(/^####\s*(.*)$/gm, this.generateReplacement('h4'));
    html = html.replace(/^###\s*(.*)$/gm, this.generateReplacement('h3'));
    html = html.replace(/^##\s*(.*)$/gm, this.generateReplacement('h2'));
    html = html.replace(/^#\s*(.*)$/gm, this.generateReplacement('h1'));

    // ========== 4. 转换无序列表 ==========
    // 第一步：标记列表项
    html = html.replace(/^\s*[-*+]\s+(.*)$/gm, '[UL_LI]$1[/UL_LI]');

    // 第二步：标记连续列表
    html = html.replace(/(\[UL_LI\][^\n]*?\[\/UL_LI\](?:\n\[UL_LI\][^\n]*?\[\/UL_LI\])*)/g, '[UL_UL]$1[/UL_UL]');

    // 第三步：转换列表项
    html = html.replace(/\[UL_LI\](.*?)\[\/UL_LI\]/g, this.generateReplacement('li'));

    // 第四步：转换列表容器
    html = html.replace(/\[UL_UL\]([\s\S]*?)\[\/UL_UL\]/g, this.generateReplacement('ul'));

    // ========== 5. 转换有序列表 ==========
    // 第一步：标记列表项
    html = html.replace(/^\s*\d+\.\s+(.*)$/gm, '[OL_LI]$1[/OL_LI]');

    // 第二步：标记连续列表
    html = html.replace(/(\[OL_LI\][^\n]*?\[\/OL_LI\](?:\n\[OL_LI\][^\n]*?\[\/OL_LI\])*)/g, '[OL_OL]$1[/OL_OL]');

    // 第三步：转换列表项
    html = html.replace(/\[OL_LI\](.*?)\[\/OL_LI\]/g, this.generateReplacement('li'));

    // 第四步：转换列表容器
    html = html.replace(/\[OL_OL\]([\s\S]*?)\[\/OL_OL\]/g, this.generateReplacement('ol'));

    return html;
  }
}

const flags1 = ['头部游戏', '黑马游戏', '潜力新游', '爆款素材', '其他动态'];
export const getCardColor = (str: string): number => {
  let index = flags1.indexOf(str);

  if (index === -1) {
    index = flags1.length - 1;
  }

  return index + 1;
};

const flags2 = [
  '备用标签',
  '开发选用',
  '技术趋势',
  '商业模式',
  '监管政策',
  '产业扶持',
  '人才动态',
  '平台生态',
  '市场报告',
  '投资风向',
  '行业大会',
  '数据服务',
  '广告买量',
];
export const getTagColor = (tag: string): number => {
  let index = flags2.indexOf(tag);

  if (index === -1) {
    index = 0;
  }

  return index + 1;
};

export const formatCasualDailyReportDate = (date?: string): string => {
  return formatDateStr(date, 'M月D日');
};

const RANK_ICONS = [IMG_Rank_01, IMG_Rank_02, IMG_Rank_03];
export const getRankElement = (i: number) => {
  const rankIcon = RANK_ICONS[i] || '';
  if (rankIcon) {
    return <img className="dbi-rank-card-index-img" src={rankIcon} />;
  }

  return <span>{i + 1}</span>;
};

export const getDodClassName = (dod?: number | null): string => {
  if (typeof dod !== 'number' || Number.isNaN(dod)) {
    return '';
  }

  if (dod > 0) {
    return 'dbi--up';
  }
  if (dod < 0) {
    return 'dbi--down';
  }
  return 'dbi--equal';
};

// 渲染异动标签
export const renderChangeTag = (tag?: string, tagDesc?: string) => {
  if (!tag) {
    return null;
  }

  const hasTagDesc = !!tagDesc?.trim();
  const tagClassName = 'dbi-update-rank-change dbi-dod--new';
  const tagContent = (
    <span style={{ cursor: hasTagDesc ? 'pointer' : 'default' }} className={tagClassName}>
      {tag}
    </span>
  );

  // 有描述时显示气泡，无描述时不显示气泡且鼠标样式为默认
  return hasTagDesc ? <Bubble content={tagDesc}>{tagContent}</Bubble> : tagContent;
};

/**
 * 解析Markdown语法的超链接（扩展语法，可以在markdown语法后通过 (键:值)的格式增加任意数量的扩展属性值）
 * @param input - markdown超链接语法文本
 */
const convertExtendedMarkdownLink = (input?: string) => {
  if (!input) {
    return '';
  }

  return input.replace(/\[([^\]]+)\]\(([^)]+)\)((?:\(([a-zA-Z\\-]+):([^)]+)\))*)/g, (_, text, href, attrsStr) => {
    const attrsList = (attrsStr?.match(/\(([a-zA-Z\\-]+):([^)]+)\)/g) || [])?.map((attr: any) => {
      return attr?.slice(1, attr?.length - 1)?.split(':') || [];
    });

    const attrs = attrsList
      ?.map(([key, val]: [string, string]) => {
        return `${key}="${val}"`;
      })
      .join(' ');

    const attrsPart = attrs ? `${attrs} ` : '';

    const isCorePointsLinker = attrsList?.some((item: [string, string]) => {
      const [key, val] = item || [];
      return key === 'data-click-action' && val === CasualDailyReportsCorePointsClickAction.ViewReportDetails;
    });
    let hrefNew = href || '';
    let attrsPartNew = attrsPart;
    // 特殊处理今日核心要点markdown文本中grc报告超连接的url（因为后端只返回一个url，但是pc端、移动端的url不一致，所以需要前端多加这一层解析处理）
    if (isCorePointsLinker) {
      const [reportSystemId, reportResourceId] = hrefNew.split('/').filter(Boolean);
      const reportDatasetList = [
        {
          key: 'data-report-system-id',
          value: reportSystemId,
        },
        {
          key: 'data-report-resource-id',
          value: reportResourceId,
        },
      ];
      const reportDatasetAttrs = reportDatasetList.map((item) => {
        const { key, value } = item || {};
        return `${key}=${value}`;
      });
      // pc端统一跳转到报告详情页中转页，在中转页根据报告id获取报告详情数据，组装好查询参数再重定向到报告详情页
      hrefNew = `/v2/intelligence/reportDetails/${reportSystemId}/${reportResourceId}`;
      attrsPartNew = `${attrsPartNew} ${reportDatasetAttrs.join(' ')}`;
    }

    return `<a ${attrsPartNew} href="${hrefNew}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });
};
